import { useEffect, useImperativeHandle, useRef, useState, forwardRef, useCallback } from 'react';
import type { PropsWithoutRef, MouseEvent, SyntheticEvent, RefObject } from 'react';
import { createPortal } from 'react-dom';

import type { ForwardRefForwardPropsComponent } from '../../types';
import { cap, getActiveElement, getFocusables, withTestIds } from '../../utils';
import Flex from '../Flex';
import MetaList from '../MetaList';
import Icon, { registerIcon } from '../Icon';
import Image from '../Image';
import Text from '../Text';
import {
  useConfiguration,
  useConsolidatedRef,
  useDirection,
  useElement,
  useEvent,
  useFocusTrap,
  useI18n,
  useScrollToggle,
  useTestIds
} from '../../hooks';
import ErrorState from '../ErrorState';
import Progress from '../Progress';
import FileVisual from '../File/FileVisual';
import Tooltip from '../Tooltip';
import Button from '../Button';
import Link from '../Link/Link';
import mimeTypes from '../File/mimeTypes.json';
import { getKindFromMimeType } from '../File/utils';
import type { AttachmentType } from '../File/utils';
import * as openIcon from '../Icon/icons/open.icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as downloadIcon from '../Icon/icons/download.icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import * as undockIcon from '../Icon/icons/undock.icon';

import {
  StyledHeader,
  StyledCountTracker,
  StyledInfo,
  StyledPreviewRegion,
  StyledNavButton,
  StyledMediaContainer,
  StyledLiveRegion,
  StyledLightbox,
  StyledContainer,
  StyledObject,
  StyledLightboxTitle
} from './Lightbox.styles';
import type { LightboxProps, MediaElement } from './Lightbox.types';
import { getLightboxTestIds } from './Lightbox.test-ids';

registerIcon(openIcon, timesIcon, downloadIcon, caretLeftIcon, caretRightIcon, undockIcon);

const LightboxComp: ForwardRefForwardPropsComponent<LightboxProps> = forwardRef(function Lightbox(
  {
    testId,
    items,
    placement = 'global',
    defaultIndex = 0,
    cycle = false,
    onItemLoad,
    onItemError,
    onNavigate,
    navigateHandle,
    onItemDownload,
    onAfterClose,
    ...restProps
  }: PropsWithoutRef<LightboxProps>,
  refArg: LightboxProps['ref']
) {
  const t = useI18n();
  const { start, end } = useDirection();
  const { portalTarget } = useConfiguration();
  const global = placement === 'global';
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const currentItem = items[currentIndex];
  const { disableScroll, enableScroll } = useScrollToggle();
  const lightboxRef = useConsolidatedRef(refArg);
  const headerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(true);
  const [lightboxTitle, setLightboxTitle] = useElement();
  const [globalFromLocalOpen, setGlobalFromLocalOpen] = useState(false);
  const mediaRef = useConsolidatedRef(currentItem.ref);
  const [liveText, setLiveText] = useState('');
  const countTrackerText = items.length > 1 ? t('x_of_y', [currentIndex + 1, items.length]) : '';
  const testIds = useTestIds(testId, getLightboxTestIds);
  const previewableAttachmentTypes = ['pdf', 'image', 'link', 'video', 'audio'];

  const initiatorRef = useRef<Element | null>(null);

  const close = () => {
    if (!global) return;
    setOpen(false);

    if (
      initiatorRef.current?.isConnected &&
      (initiatorRef.current instanceof HTMLElement || initiatorRef.current instanceof SVGElement)
    )
      initiatorRef.current.focus();
  };

  useImperativeHandle(navigateHandle, () => ({
    navigate: index => {
      if (!items.length) return;
      const clampedIndex = Math.min(Math.max(index, 0), items.length - 1);
      setCurrentIndex(clampedIndex);
    }
  }));

  const navigate = (direction: 1 | -1) => {
    if (direction === 1) {
      if (currentIndex < items.length - 1) {
        setCurrentIndex(cur => cur + 1);
      } else if (cycle) {
        setCurrentIndex(0);
      }
    } else if (currentIndex > 0) {
      setCurrentIndex(cur => cur - 1);
    } else if (cycle) {
      setCurrentIndex(items.length - 1);
    }
  };
  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.repeat) return;
    if (!lightboxRef.current?.contains(getActiveElement())) return;

    if (e.key === 'Escape') {
      close();
    } else if (e.key === `Arrow${cap(end)}`) {
      navigate(1);
    } else if (e.key === `Arrow${cap(start)}`) {
      navigate(-1);
    }
  };

  useEvent('keydown', keyDownHandler);

  useFocusTrap(global ? lightboxRef : null);

  useEffect(() => {
    setLiveText(`${currentItem.name}. ${countTrackerText ? `${countTrackerText}.` : ''}`);
  }, [currentItem.name, countTrackerText]);

  useEffect(() => {
    onNavigate?.(currentItem.id, currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    const activeElement = getActiveElement();
    initiatorRef.current = activeElement;
    if (getFocusables(lightboxRef).length > 0) getFocusables(lightboxRef)[0].focus();
  }, []);

  const handleLoad = useCallback(
    (e: SyntheticEvent<MediaElement> | Event) => {
      onItemLoad?.(currentItem.id, e);
    },
    [onItemLoad, currentItem.id]
  );

  const handleError = useCallback(
    (e: SyntheticEvent<MediaElement> | Event) => {
      onItemError?.(currentItem.id, e);
    },
    [onItemError, currentItem.id]
  );

  // Object elements appear to need native event handlers bound as JSX does not work.
  // TODO: Follow up on reason why.
  useEffect(() => {
    if (!mediaRef.current || currentItem.format !== 'pdf') return;
    const pdfObject = mediaRef.current;
    pdfObject.addEventListener('load', handleLoad);
    pdfObject.addEventListener('error', handleError);

    return () => {
      pdfObject.removeEventListener('load', handleLoad);
      pdfObject.removeEventListener('error', handleError);
    };
  }, [currentItem]);

  let media;
  let validContent = true;
  let attachmentType: AttachmentType = 'generic';
  if (currentItem.format === undefined) {
    validContent = !currentItem.error;
  } else {
    const extractMimeType = (mimeTypes as Record<string, string>)[currentItem.format.toLowerCase()];
    const getType = currentItem.format === 'url' ? 'link' : 'generic';
    attachmentType = extractMimeType ? getKindFromMimeType(extractMimeType) : getType;
    validContent =
      currentItem.disablePreview ||
      (previewableAttachmentTypes.includes(attachmentType) ? !!currentItem.src : !!attachmentType);
  }

  if (validContent) {
    const nonPreviewableContent = (
      <>
        <Text>
          {t(currentItem.disablePreview ? 'large_non_previewable' : 'non_previewable', [
            currentItem.format ? currentItem.format.toUpperCase() : currentItem.name
          ])}
        </Text>
        {onItemDownload && (
          <Button
            aria-label={t('download')}
            onClick={() => onItemDownload(currentItem.id)}
            data-testid={testIds.nonPreviewableDownload}
          >
            {t('download')}
          </Button>
        )}
      </>
    );

    const defaultContent = (
      <Flex
        container={{ direction: 'column', gap: 2, alignItems: 'center' }}
        as={StyledInfo}
        data-testid={testIds.content}
      >
        <FileVisual type={attachmentType} size='l' />
        {!currentItem.disablePreview && attachmentType === 'link' && currentItem.src ? (
          <Link href={currentItem.src} data-testid={testIds.link}>
            {currentItem.name}
          </Link>
        ) : (
          nonPreviewableContent
        )}
      </Flex>
    );

    const mediaType = currentItem.disablePreview ? undefined : attachmentType;
    switch (mediaType) {
      case 'image':
        media = currentItem.src && (
          <Image
            data-testid={testIds.content}
            src={currentItem.src}
            alt={currentItem.description ?? t('description_unavailable')}
            onLoad={handleLoad}
            onError={handleError}
            ref={mediaRef as RefObject<HTMLImageElement>}
          />
        );
        break;
      case 'pdf':
        media = currentItem.src && (
          <>
            {navigator.pdfViewerEnabled ? (
              <StyledObject
                data-testid={testIds.content}
                data={currentItem.src}
                type='application/pdf'
                ref={mediaRef as RefObject<HTMLObjectElement>}
              >
                {currentItem.description ?? t('description_unavailable')}
              </StyledObject>
            ) : (
              defaultContent
            )}
          </>
        );
        break;
      case 'video':
        media = currentItem.src && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            data-testid={testIds.content}
            src={currentItem.src}
            controls
            onLoadedData={handleLoad}
            onError={handleError}
            ref={mediaRef as RefObject<HTMLVideoElement>}
          />
        );
        break;
      case 'audio':
        media = currentItem.src && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            data-testid={testIds.content}
            src={currentItem.src}
            controls
            onLoadedData={handleLoad}
            onError={handleError}
            ref={mediaRef as RefObject<HTMLAudioElement>}
          />
        );
        break;
      default: {
        media = defaultContent;
      }
    }
  } else {
    media = <Progress placement='block' delay />;
  }

  const content = (
    <StyledLightbox
      data-testid={testIds.root}
      {...restProps}
      tabIndex={-1}
      ref={lightboxRef}
      open={open}
      global={global}
      role={global ? 'dialog' : undefined}
      aria-modal={global ? true : undefined}
      container={{ alignItems: 'start', justify: 'start', direction: 'column' }}
      alpha={0.9}
      variant='dark'
      position={global ? 'fixed' : 'absolute'}
      transitionSpeed='slow'
      onBeforeTransitionIn={global ? disableScroll : undefined}
      onAfterTransitionOut={() => {
        if (global) {
          enableScroll();
        }
        onAfterClose?.();
      }}
    >
      <Flex
        as={StyledHeader}
        ref={headerRef}
        global={global}
        role='none'
        container={{
          justify: 'between',
          alignItems: 'center',
          gap: 2
        }}
        onMouseDown={(e: MouseEvent<HTMLDivElement>) => {
          if (e.button !== 0) return;
          if (e.target === e.currentTarget) close();
        }}
      >
        <StyledCountTracker data-testid={testIds.pagination} aria-hidden>
          {countTrackerText}
        </StyledCountTracker>

        <Flex
          as={StyledInfo}
          container={{
            direction: 'column',
            wrap: 'wrap',
            alignItems: 'center',
            justify: 'center',
            colGap: 2,
            rowGap: 0.5
          }}
        >
          <StyledLightboxTitle
            data-testid={testIds.name}
            variant='primary'
            as='h2'
            ref={setLightboxTitle}
          >
            {currentItem.name}
          </StyledLightboxTitle>
          {currentItem.metadata && (
            <MetaList data-testid={testIds.metadata} items={currentItem.metadata} />
          )}
        </Flex>

        <Flex container as={StyledContainer}>
          {attachmentType !== 'link' && onItemDownload && (
            <Button
              data-testid={testIds.download}
              variant='simple'
              label={t('download')}
              icon
              onClick={() => onItemDownload(currentItem.id)}
            >
              <Icon name='download' />
            </Button>
          )}
          {!currentItem.disablePreview &&
            previewableAttachmentTypes.includes(attachmentType) &&
            currentItem.src && (
              <Button
                data-testid={testIds.openInNewTab}
                variant='simple'
                label={t('link_open_in_tab_text')}
                icon
                href={currentItem.src}
                target='_blank'
              >
                <Icon name='open' />
              </Button>
            )}
          {placement === 'global' && (
            <Button
              data-testid={testIds.close}
              variant='simple'
              ref={closeButtonRef}
              label={t('close')}
              icon
              onClick={close}
            >
              <Icon name='times' />
            </Button>
          )}
          {placement === 'local' && (
            <Button
              data-testid={testIds.close}
              variant='simple'
              label={t('enter_fullscreen')}
              icon
              onClick={() => setGlobalFromLocalOpen(true)}
            >
              <Icon name='undock' />
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex
        container={{ justify: 'between' }}
        as={StyledPreviewRegion}
        item={{ grow: 1, shrink: 0 }}
      >
        {items.length > 1 && (
          <StyledNavButton
            data-testid={testIds.prev}
            ref={prevButtonRef}
            variant='simple'
            aria-label={t('pagination_prev')}
            hidden={!cycle && currentIndex === 0}
            icon
            onClick={() => {
              navigate(-1);
            }}
          >
            <span>
              <Icon name='caret-left' />
            </span>
          </StyledNavButton>
        )}

        <Flex item={{ grow: 1 }} as={StyledMediaContainer} global={global}>
          <Flex
            container={{ alignItems: 'center', justify: 'center' }}
            onMouseDown={(e: MouseEvent<HTMLDivElement>) => {
              if (e.button !== 0) return;
              if (e.target === e.currentTarget) close();
            }}
          >
            {currentItem.error ? <ErrorState message={t('content_load_error')} /> : media}
          </Flex>
        </Flex>

        {items.length > 1 && (
          <StyledNavButton
            data-testid={testIds.next}
            ref={nextButtonRef}
            variant='simple'
            aria-label={t('pagination_next')}
            hidden={!cycle && currentIndex === items.length - 1}
            icon
            onClick={() => {
              navigate(1);
            }}
          >
            <span>
              <Icon name='caret-right' />
            </span>
          </StyledNavButton>
        )}
      </Flex>
      {!global && (
        <Tooltip smart showDelay='none' hideDelay='none' target={lightboxTitle}>
          {currentItem.name}
        </Tooltip>
      )}
      <StyledLiveRegion aria-live='polite' aria-label={liveText} />
    </StyledLightbox>
  );

  const globalFromLocalLightbox = (
    <LightboxComp
      items={items}
      placement='global'
      defaultIndex={currentIndex}
      cycle={cycle}
      onItemLoad={onItemLoad}
      onItemError={onItemError}
      onNavigate={onNavigate}
      onItemDownload={onItemDownload}
      onAfterClose={() => setGlobalFromLocalOpen(false)}
    />
  );

  return (
    <>
      {global && portalTarget ? createPortal(content, portalTarget) : content}
      {globalFromLocalOpen && globalFromLocalLightbox}
    </>
  );
});

export default withTestIds(LightboxComp, getLightboxTestIds);
